/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import { useMutation } from 'react-query'

import type { QueryFunctionReturnType } from '@modules/query/types'
import { invalidateGetBudgets } from '@modules/billing-lib/queries/useGetBudgets'
import { createBudgetV1 } from '@modules/billing-api/v1/callers'
import type { operations } from '@modules/billing-api/v1/types'

import type { UseMutationOptions } from 'react-query'

type Alerts = operations['createBudgetV1']['requestBody']['content']['application/json']['alerts']
type Scope =
  operations['createBudgetV1']['requestBody']['content']['application/json']['scope_type']
type Period = operations['createBudgetV1']['requestBody']['content']['application/json']['period']

const FIXED_ALERTS: Alerts = [
  {
    operator: 'gte',
    threshold: 75,
    threshold_type: 'percentage',
  },
  {
    operator: 'gte',
    threshold: 100,
    threshold_type: 'percentage',
  },
]
const FIXED_SCOPE: Scope = 'organization'
const FIXED_PERIOD: Period = 'monthly'

// TODO: update once create budget R1 is deprecated
export const defaultCreateBudgetValues: Omit<CreateBudgetBody, 'amount' | 'scope_values'> = {
  alerts: FIXED_ALERTS,
  scope_type: FIXED_SCOPE,
  period: FIXED_PERIOD,
  active: true,
}

export type CreateBudgetBody =
  operations['createBudgetV1']['requestBody']['content']['application/json']

type CreateBudgetVariables = {
  organizationId: operations['createBudgetV1']['parameters']['path']['organization_id']
  body: CreateBudgetBody
}

const useCreateBudget = (
  options?: UseMutationOptions<
    QueryFunctionReturnType<typeof createBudgetV1>,
    unknown,
    CreateBudgetVariables
  >,
) =>
  useMutation<QueryFunctionReturnType<typeof createBudgetV1>, unknown, CreateBudgetVariables>({
    mutationFn: ({ organizationId, body }) =>
      createBudgetV1({
        pathParameters: { organization_id: organizationId },
        body,
      }),
    onSuccess: (data, variables, context) => {
      invalidateGetBudgets(variables.organizationId)
      options?.onSuccess?.(data, variables, context)
    },
  })

export default useCreateBudget
